/**
 * GlobalView — ContextPanel (HUD) Component
 *
 * Right-side context display (§11.6).
 * Shows:
 *  - CoordDisplay: mouse cursor geo-position
 *  - DataReadout: selected entity details
 *  - Camera position & view mode
 *  - Keyboard shortcuts hint
 */

import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import styles from './ContextPanel.module.css';
import StatusBadge from '../ui/StatusBadge';

interface ContextPanelProps {
  collapsed?: boolean;
}

/** Format decimal degrees */
function fmt(val: number, decimals = 5): string {
  return val.toFixed(decimals);
}

/** Format altitude */
function fmtAlt(meters: number): string {
  if (meters >= 1_000_000) return `${(meters / 1_000_000).toFixed(1)} Mm`;
  if (meters >= 1_000) return `${(meters / 1_000).toFixed(1)} km`;
  return `${Math.round(meters)} m`;
}

const VIEW_MODE_LABELS = {
  globe: 'GLOBE',
  region: 'REGION',
  city: 'CITY',
} as const;

// ─── CoordDisplay Component (§2.5) ─────────────────────────────

function CoordDisplay() {
  const cursorPosition = useGlobeStore((s) => s.cursorPosition);

  if (!cursorPosition) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionLabel}>CURSOR</div>
        <div className={styles.empty}>Maus über Globe bewegen</div>
      </div>
    );
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>CURSOR</div>
      <div className={styles.dataRow}>
        <span className={styles.dataLabel}>LAT</span>
        <span className={styles.dataValue}>{fmt(cursorPosition.lat)}</span>
      </div>
      <div className={styles.dataRow}>
        <span className={styles.dataLabel}>LON</span>
        <span className={styles.dataValue}>{fmt(cursorPosition.lon)}</span>
      </div>
    </div>
  );
}

// ─── DataReadout Component (§2.5) ──────────────────────────────

function DataReadout() {
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );

  if (!selectedEntityId) {
    return (
      <div className={styles.section}>
        <div className={styles.sectionLabel}>SELECTION</div>
        <div className={styles.empty}>Kein Objekt ausgewählt</div>
      </div>
    );
  }

  // Aircraft-specific details
  const props = entity?.properties as Record<string, unknown> | undefined;
  const callsign = props?.callsign as string | undefined;
  const baroAlt = props?.baroAltitude as number | undefined;
  const velocity = props?.velocity as number | undefined;
  const origin = props?.origin as string | undefined;
  const flightLevel = baroAlt != null ? `FL${Math.round(baroAlt / 30.48)}` : '—';
  const speedKt = velocity != null ? `${Math.round(velocity * 1.944)} kt` : '—';

  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>SELECTION</div>
      <div className={styles.dataRow}>
        <span className={styles.dataLabel}>ID</span>
        <span className={styles.dataValueMono}>
          {callsign || selectedEntityId}
        </span>
      </div>
      {entity?.type === 'aircraft' && (
        <>
          <div className={styles.dataRow}>
            <span className={styles.dataLabel}>ALT</span>
            <span className={styles.dataValue}>{flightLevel}</span>
          </div>
          <div className={styles.dataRow}>
            <span className={styles.dataLabel}>SPD</span>
            <span className={styles.dataValue}>{speedKt}</span>
          </div>
          {origin && (
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>ORG</span>
              <span className={styles.dataValue}>{origin}</span>
            </div>
          )}
        </>
      )}
      {!entity && (
        <div className={styles.dataRow}>
          <span className={styles.dataLabel}>TYP</span>
          <span className={styles.dataValue}>Entity</span>
        </div>
      )}
    </div>
  );
}

// ─── Shortcut Hints ────────────────────────────────────────────

function ShortcutHints() {
  const hints = [
    { key: 'R', label: 'Reset View' },
    { key: 'H', label: 'Home (Zürich)' },
    { key: '+/−', label: 'Zoom' },
    { key: 'ESC', label: 'Deselect' },
  ];

  return (
    <div className={styles.section}>
      <div className={styles.sectionLabel}>SHORTCUTS</div>
      {hints.map((h) => (
        <div key={h.key} className={styles.dataRow}>
          <span className={styles.shortcutKey}>{h.key}</span>
          <span className={styles.shortcutLabel}>{h.label}</span>
        </div>
      ))}
    </div>
  );
}

// ─── Main Panel ────────────────────────────────────────────────

export default function ContextPanel({ collapsed }: ContextPanelProps) {
  const cameraPosition = useGlobeStore((s) => s.cameraPosition);
  const viewMode = useGlobeStore((s) => s.viewMode);
  const ready = useGlobeStore((s) => s.ready);
  const tileMode = useGlobeStore((s) => s.tileMode);

  return (
    <aside
      className={`${styles.panel} ${collapsed ? styles.collapsed : ''}`}
      id="context-panel"
    >
      {/* HUD bracket corners */}
      <span className={styles.bracketTR} />
      <span className={styles.bracketBR} />
      {/* SELECTION / DataReadout */}
      <DataReadout />

      <div className={styles.divider} />

      {/* CURSOR / CoordDisplay */}
      <CoordDisplay />

      <div className={styles.divider} />

      {/* CAMERA POSITION */}
      <div className={styles.section}>
        <div className={styles.sectionLabel}>KAMERA</div>
        {ready ? (
          <>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>LAT</span>
              <span className={styles.dataValue}>{fmt(cameraPosition.lat)}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>LON</span>
              <span className={styles.dataValue}>{fmt(cameraPosition.lon)}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>ALT</span>
              <span className={styles.dataValue}>{fmtAlt(cameraPosition.alt)}</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>HDG</span>
              <span className={styles.dataValue}>{fmt(cameraPosition.heading, 1)}°</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>PITCH</span>
              <span className={styles.dataValue}>{fmt(cameraPosition.pitch, 1)}°</span>
            </div>
          </>
        ) : (
          <>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>LAT</span>
              <span className={styles.dataValue}>—</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>LON</span>
              <span className={styles.dataValue}>—</span>
            </div>
            <div className={styles.dataRow}>
              <span className={styles.dataLabel}>ALT</span>
              <span className={styles.dataValue}>—</span>
            </div>
          </>
        )}
      </div>

      <div className={styles.divider} />

      {/* STATUS */}
      <div className={styles.section}>
        <div className={styles.sectionLabel}>STATUS</div>
        <div className={styles.dataRow}>
          <span className={styles.dataLabel}>MODE</span>
          <StatusBadge variant="online" label="LIVE" pulse />
        </div>
        <div className={styles.dataRow}>
          <span className={styles.dataLabel}>VIEW</span>
          <span className={styles.dataValueMono}>
            {VIEW_MODE_LABELS[viewMode]}
          </span>
        </div>
        <div className={styles.dataRow}>
          <span className={styles.dataLabel}>3D</span>
          <span className={styles.dataValueMono}>
            {tileMode === 'google3d' ? 'GOOGLE' : 'STD'}
          </span>
        </div>
        <div className={styles.dataRow}>
          <span className={styles.dataLabel}>LAYERS</span>
          <span className={styles.dataValueMono}>0</span>
        </div>
      </div>

      <div className={styles.divider} />

      {/* SHORTCUTS */}
      <ShortcutHints />
    </aside>
  );
}

